import React, { useState, useEffect, useCallback } from 'react';
import { Save, RefreshCw, Plus, Trash2, DollarSign, AlertTriangle, CheckCircle, Upload, Shield } from 'lucide-react';
import { pricing as defaultPricing, domainLabels } from './data/pricing';

const CATEGORIES = [
  { key: 'platform', label: 'Platform', type: 'platform' },
  { key: 'deployment', label: 'Deployment', type: 'keyValue' },
  { key: 'users', label: 'Users', type: 'keyValue' },
  { key: 'reportsEBS', label: 'EBS Reports', type: 'domainGrouped' },
  { key: 'reportsCloud', label: 'Cloud Reports', type: 'domainGrouped' },
  { key: 'financialAnalytics', label: 'Financial Analytics', type: 'flatArray' },
  { key: 'peopleAnalytics', label: 'People Analytics', type: 'flatArrayPEPM' },
  { key: 'glConnect', label: 'GL Connect', type: 'keyValue' },
  { key: 'crm', label: 'CRM', type: 'flatArray' },
  { key: 'erpConnectors', label: 'ERP Connectors', type: 'flatArray' },
  { key: 'dataPipelines', label: 'Data Pipelines', type: 'flatArray' },
  { key: 'utilities', label: 'Utilities', type: 'flatArray' },
  { key: 'services', label: 'Services', type: 'keyValue' },
];

const PricingCatalogView = ({ onPricingUpdated }) => {
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [activeTab, setActiveTab] = useState('platform');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchPricing = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_URL}/api/pricing`, { headers, credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPricingData(data.pricing ? JSON.parse(JSON.stringify(data.pricing)) : JSON.parse(JSON.stringify(defaultPricing)));
      } else if (response.status === 403) {
        setPricingData(null);
      }
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
      setPricingData(JSON.parse(JSON.stringify(defaultPricing)));
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { fetchPricing(); }, [fetchPricing]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/admin/pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) },
        credentials: 'include',
        body: JSON.stringify({ pricing: pricingData })
      });
      if (response.ok) {
        setHasChanges(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        onPricingUpdated?.();
      } else {
        const err = await response.json();
        setSaveError(err.error || 'Failed to save');
      }
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/admin/pricing/seed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) },
        credentials: 'include',
        body: JSON.stringify({ pricing: defaultPricing })
      });
      if (response.ok) {
        setPricingData(JSON.parse(JSON.stringify(defaultPricing)));
        setHasChanges(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        onPricingUpdated?.();
      }
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Generic update helper
  const updatePricing = (updater) => {
    setPricingData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      updater(next);
      return next;
    });
    setHasChanges(true);
    setSaveSuccess(false);
  };

  // ─── Platform editor (split: New Customer / Existing Customer) ───
  const renderPlatformEditor = () => {
    const data = pricingData?.platform;
    if (!data) return null;

    return (
      <div className="space-y-8">
        {/* New Customer (Prospect) Section */}
        <div className="border border-blue-200 rounded-lg overflow-hidden">
          <div className="bg-blue-50 px-4 py-3 flex items-center gap-2">
            <span className="inline-flex px-2 py-0.5 bg-blue-600 text-white rounded text-xs font-semibold">New Customer</span>
            <span className="text-sm font-semibold text-blue-800">Prospect Bundle Pricing</span>
          </div>
          <div className="p-4">
            <p className="text-xs text-slate-500 mb-3">When a new customer selects both SplashBI + SplashAI, they get the combined bundle price.</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-2 font-semibold text-slate-700">Name</th>
                  <th className="text-left px-4 py-2 font-semibold text-slate-700">SKU</th>
                  <th className="text-right px-4 py-2 font-semibold text-slate-700">Bundle Price ($)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={data.prospectBundle?.name || 'SplashBIxAI Platform'}
                      onChange={(e) => updatePricing(d => {
                        if (!d.platform.prospectBundle) d.platform.prospectBundle = { price: 100000, sku: '', name: '' };
                        d.platform.prospectBundle.name = e.target.value;
                      })}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={data.prospectBundle?.sku || ''}
                      onChange={(e) => updatePricing(d => {
                        if (!d.platform.prospectBundle) d.platform.prospectBundle = { price: 100000, sku: '', name: '' };
                        d.platform.prospectBundle.sku = e.target.value;
                      })}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="number"
                      value={data.prospectBundle?.price ?? 100000}
                      onChange={(e) => updatePricing(d => {
                        if (!d.platform.prospectBundle) d.platform.prospectBundle = { price: 0, sku: '', name: '' };
                        d.platform.prospectBundle.price = parseFloat(e.target.value) || 0;
                      })}
                      className="w-32 px-2 py-1 border border-slate-200 rounded text-sm text-right focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Existing Customer Section */}
        <div className="border border-green-200 rounded-lg overflow-hidden">
          <div className="bg-green-50 px-4 py-3 flex items-center gap-2">
            <span className="inline-flex px-2 py-0.5 bg-green-600 text-white rounded text-xs font-semibold">Existing Customer</span>
            <span className="text-sm font-semibold text-green-800">Individual Platform Pricing</span>
          </div>
          <div className="p-4">
            <p className="text-xs text-slate-500 mb-3">Existing customers purchase each platform separately.</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-2 font-semibold text-slate-700">Platform</th>
                  <th className="text-left px-4 py-2 font-semibold text-slate-700">SKU</th>
                  <th className="text-right px-4 py-2 font-semibold text-slate-700">Price ($)</th>
                </tr>
              </thead>
              <tbody>
                {['SplashBI', 'SplashAI'].map(key => (
                  <tr key={key} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{key}</td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={data[key]?.sku || ''}
                        onChange={(e) => updatePricing(d => { d.platform[key].sku = e.target.value; })}
                        className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input
                        type="number"
                        value={data[key]?.price ?? 0}
                        onChange={(e) => updatePricing(d => { d.platform[key].price = parseFloat(e.target.value) || 0; })}
                        className="w-32 px-2 py-1 border border-slate-200 rounded text-sm text-right focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ─── Key-Value editors (deployment, users, glConnect, services) ───
  const renderKeyValueEditor = (categoryKey) => {
    const data = pricingData?.[categoryKey];
    if (!data || typeof data !== 'object') return null;

    const userLabels = {
      admin: 'Developer/Admin', readOnly: 'Read Only', aiUsers: 'AI Users',
      mobile: 'Mobile App', excel: 'Excel Connect', sqlConnectEnt: 'SQL Connect Enterprise',
      glConnect: 'GL Connect'
    };

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 font-semibold text-slate-700">Name</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700">SKU</th>
              <th className="text-right px-4 py-3 font-semibold text-slate-700">Price ($)</th>
              {categoryKey === 'users' && <th className="text-right px-4 py-3 font-semibold text-slate-700">Monthly</th>}
              {categoryKey === 'deployment' && <th className="text-left px-4 py-3 font-semibold text-slate-700">Description</th>}
              <th className="text-center px-4 py-3 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data).map(([key, item]) => (
              <tr key={key} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  {categoryKey === 'glConnect' || categoryKey === 'services' ? (
                    <span className="font-medium text-slate-800">{item.name || key}</span>
                  ) : (
                    <span className="font-medium text-slate-800">{userLabels[key] || key}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={item.sku || ''}
                    onChange={(e) => updatePricing(d => { d[categoryKey][key].sku = e.target.value; })}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <input
                    type="number"
                    value={item.price ?? 0}
                    onChange={(e) => updatePricing(d => { d[categoryKey][key].price = parseFloat(e.target.value) || 0; })}
                    className="w-28 px-2 py-1 border border-slate-200 rounded text-sm text-right focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  />
                </td>
                {categoryKey === 'users' && (
                  <td className="px-4 py-3 text-right">
                    {item.monthly !== undefined && (
                      <input
                        type="number"
                        value={item.monthly ?? 0}
                        onChange={(e) => updatePricing(d => { d[categoryKey][key].monthly = parseFloat(e.target.value) || 0; })}
                        className="w-28 px-2 py-1 border border-slate-200 rounded text-sm text-right focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                      />
                    )}
                  </td>
                )}
                {categoryKey === 'deployment' && (
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={item.desc || ''}
                      onChange={(e) => updatePricing(d => { d[categoryKey][key].desc = e.target.value; })}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                    />
                  </td>
                )}
                <td className="px-4 py-3 text-center">
                  {categoryKey === 'services' && (
                    <button
                      onClick={() => setDeleteConfirm({ category: categoryKey, key })}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {categoryKey === 'services' && (
          <button
            onClick={() => {
              const name = prompt('Service name:');
              if (name) {
                updatePricing(d => {
                  d.services[name] = { price: 0, sku: '', isOneTime: true };
                });
              }
            }}
            className="mt-3 flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
          >
            <Plus size={14} /> Add Service
          </button>
        )}
      </div>
    );
  };

  // ─── Flat array editors (financialAnalytics, crm, erpConnectors, dataPipelines, utilities) ───
  const renderFlatArrayEditor = (categoryKey) => {
    const items = pricingData?.[categoryKey];
    if (!Array.isArray(items)) return null;

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 font-semibold text-slate-700">Name</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700">SKU</th>
              <th className="text-right px-4 py-3 font-semibold text-slate-700">Price ($)</th>
              <th className="text-center px-4 py-3 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={item.name || ''}
                    onChange={(e) => updatePricing(d => { d[categoryKey][idx].name = e.target.value; })}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={item.sku || ''}
                    onChange={(e) => updatePricing(d => { d[categoryKey][idx].sku = e.target.value; })}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <input
                    type="number"
                    value={item.price ?? 0}
                    onChange={(e) => updatePricing(d => { d[categoryKey][idx].price = parseFloat(e.target.value) || 0; })}
                    className="w-28 px-2 py-1 border border-slate-200 rounded text-sm text-right focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => setDeleteConfirm({ category: categoryKey, index: idx, name: item.name })}
                    className="text-red-400 hover:text-red-600 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          onClick={() => updatePricing(d => { d[categoryKey].push({ name: '', price: 0, sku: '' }); })}
          className="mt-3 flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
        >
          <Plus size={14} /> Add Product
        </button>
      </div>
    );
  };

  // ─── PEPM editor (peopleAnalytics) ───
  const renderPEPMEditor = (categoryKey) => {
    const items = pricingData?.[categoryKey];
    if (!Array.isArray(items)) return null;

    return (
      <div className="overflow-x-auto">
        <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700">
          People Analytics uses PEPM (Per Employee Per Month) pricing. Total PEPM: $
          {items.reduce((sum, i) => sum + (i.pepm || 0), 0).toFixed(2)}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 font-semibold text-slate-700">Name</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700">SKU</th>
              <th className="text-right px-4 py-3 font-semibold text-slate-700">PEPM ($)</th>
              <th className="text-center px-4 py-3 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={item.name || ''}
                    onChange={(e) => updatePricing(d => { d[categoryKey][idx].name = e.target.value; })}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={item.sku || ''}
                    onChange={(e) => updatePricing(d => { d[categoryKey][idx].sku = e.target.value; })}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <input
                    type="number"
                    step="0.01"
                    value={item.pepm ?? 0}
                    onChange={(e) => updatePricing(d => {
                      d[categoryKey][idx].pepm = parseFloat(e.target.value) || 0;
                      d[categoryKey][idx].isPEPM = true;
                    })}
                    className="w-28 px-2 py-1 border border-slate-200 rounded text-sm text-right focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => setDeleteConfirm({ category: categoryKey, index: idx, name: item.name })}
                    className="text-red-400 hover:text-red-600 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          onClick={() => updatePricing(d => { d[categoryKey].push({ name: '', pepm: 0, sku: '', isPEPM: true }); })}
          className="mt-3 flex items-center gap-1 px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-lg"
        >
          <Plus size={14} /> Add Module
        </button>
      </div>
    );
  };

  // ─── Domain-grouped editors (reportsEBS, reportsCloud) ───
  const renderDomainGroupedEditor = (categoryKey) => {
    const data = pricingData?.[categoryKey];
    if (!data || typeof data !== 'object') return null;

    return (
      <div className="space-y-6">
        {Object.entries(data).map(([domain, items]) => (
          <div key={domain} className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-100 px-4 py-2 font-semibold text-slate-700 text-sm">
              {domainLabels[domain] || domain.toUpperCase()}
              <span className="ml-2 text-slate-400 font-normal">({items.length} modules)</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Name</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">SKU</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">Price ($)</th>
                  <th className="text-center px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={item.name || ''}
                        onChange={(e) => updatePricing(d => { d[categoryKey][domain][idx].name = e.target.value; })}
                        className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={item.sku || ''}
                        onChange={(e) => updatePricing(d => { d[categoryKey][domain][idx].sku = e.target.value; })}
                        className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <input
                        type="number"
                        value={item.price ?? 0}
                        onChange={(e) => updatePricing(d => { d[categoryKey][domain][idx].price = parseFloat(e.target.value) || 0; })}
                        className="w-28 px-2 py-1 border border-slate-200 rounded text-sm text-right focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => setDeleteConfirm({ category: categoryKey, domain, index: idx, name: item.name })}
                        className="text-red-400 hover:text-red-600 p-1"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-2 bg-slate-50">
              <button
                onClick={() => updatePricing(d => { d[categoryKey][domain].push({ name: '', price: 0, sku: '' }); })}
                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
              >
                <Plus size={12} /> Add Module
              </button>
            </div>
          </div>
        ))}
        <button
          onClick={() => {
            const domain = prompt('Domain key (e.g., hcm, fin, scm):');
            if (domain) {
              updatePricing(d => {
                if (!d[categoryKey][domain]) {
                  d[categoryKey][domain] = [];
                }
              });
            }
          }}
          className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
        >
          <Plus size={14} /> Add Domain
        </button>
      </div>
    );
  };

  // ─── Delete handler ───
  const handleDelete = () => {
    if (!deleteConfirm) return;
    const { category, key, domain, index } = deleteConfirm;
    updatePricing(d => {
      if (key !== undefined) {
        delete d[category][key];
      } else if (domain !== undefined) {
        d[category][domain].splice(index, 1);
      } else {
        d[category].splice(index, 1);
      }
    });
    setDeleteConfirm(null);
  };

  // ─── Render active tab content ───
  const renderTabContent = () => {
    const cat = CATEGORIES.find(c => c.key === activeTab);
    if (!cat) return null;

    switch (cat.type) {
      case 'platform': return renderPlatformEditor();
      case 'keyValue': return renderKeyValueEditor(activeTab);
      case 'flatArray': return renderFlatArrayEditor(activeTab);
      case 'flatArrayPEPM': return renderPEPMEditor(activeTab);
      case 'domainGrouped': return renderDomainGroupedEditor(activeTab);
      default: return null;
    }
  };

  // Count items per category
  const getItemCount = (key) => {
    const data = pricingData?.[key];
    if (!data) return 0;
    if (Array.isArray(data)) return data.length;
    if (typeof data === 'object') {
      // Check if values are arrays (domain-grouped)
      const values = Object.values(data);
      if (values.length > 0 && Array.isArray(values[0])) {
        return values.reduce((sum, arr) => sum + arr.length, 0);
      }
      return Object.keys(data).length;
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12 text-center">
        <RefreshCw className="animate-spin mx-auto mb-4 text-blue-500" size={32} />
        <p className="text-slate-500">Loading pricing catalog...</p>
      </div>
    );
  }

  if (!pricingData) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <Shield size={48} className="mx-auto mb-4 text-red-400" />
          <h2 className="text-xl font-bold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">You don't have permission to view the product pricing catalog.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <DollarSign className="text-blue-600" size={28} />
            Product Pricing Catalog
          </h1>
          <p className="text-slate-500 mt-1">Manage product SKUs and pricing across all categories</p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-sm text-amber-600 flex items-center gap-1">
              <AlertTriangle size={14} />
              Unsaved changes
            </span>
          )}
          {saveSuccess && (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle size={14} />
              Saved
            </span>
          )}
          {saveError && (
            <span className="text-sm text-red-600">{saveError}</span>
          )}
          <button
            onClick={handleSeed}
            className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg flex items-center gap-1"
            title="Reset to default pricing"
          >
            <Upload size={14} />
            Seed Defaults
          </button>
          <button
            onClick={fetchPricing}
            className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg flex items-center gap-1"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
              hasChanges
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            <Save size={14} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Main layout: tabs + content */}
      <div className="flex gap-6">
        {/* Sidebar tabs */}
        <div className="w-52 flex-shrink-0">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {CATEGORIES.map(cat => (
              <button
                key={cat.key}
                onClick={() => setActiveTab(cat.key)}
                className={`w-full text-left px-4 py-3 text-sm border-b border-slate-100 last:border-b-0 transition-colors ${
                  activeTab === cat.key
                    ? 'bg-blue-50 text-blue-700 font-semibold border-l-2 border-l-blue-600'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{cat.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    activeTab === cat.key ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {getItemCount(cat.key)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            {CATEGORIES.find(c => c.key === activeTab)?.label}
          </h2>
          {renderTabContent()}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Delete Product</h3>
            <p className="text-slate-600 text-sm mb-4">
              Remove <span className="font-semibold">{deleteConfirm.name || deleteConfirm.key}</span>? This won't be saved until you click "Save Changes".
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingCatalogView;
